import { Alert, Button } from '@catering/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useState } from 'react';

import { hasPermission } from '../../../auth/has-permission';
import { useCurrentUser } from '../../../auth/use-current-user';
import type { Allergen, MenuDetail } from '../../../menus/api';
import { updateMenu } from '../../../menus/api';
import { mapMenuPatchError } from '../menu-form-schema';

export interface MenuAllergensTabProps {
  menu: MenuDetail;
  allergens: Allergen[];
}

/**
 * `menus/spec.md` → "Declaración de alérgenos", "Validación de coherencia
 * dietaria". Same pattern as `MenuDietaryTagsTab`: the declared list always
 * comes from server data, never from local state.
 */
export function MenuAllergensTab({ menu, allergens }: MenuAllergensTabProps): JSX.Element {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState('');
  const currentUser = useCurrentUser();
  const readOnly = menu.deletedAt !== null || !hasPermission(currentUser.data, 'menu.update');

  const mutation = useMutation({
    mutationFn: (allergenIds: string[]) => updateMenu(menu.id, { allergenIds }),
    onSuccess: async () => {
      setSelected('');
      await queryClient.invalidateQueries({ queryKey: ['menu', menu.id] });
    },
  });

  const declaredIds = new Set(menu.allergens.map((allergen) => allergen.id));
  const availableOptions = allergens.filter(
    (allergen) => allergen.isActive && !declaredIds.has(allergen.id),
  );

  function addAllergen(): void {
    if (selected === '') {
      return;
    }

    mutation.mutate([...declaredIds, selected]);
  }

  function removeAllergen(allergenId: string): void {
    mutation.mutate([...declaredIds].filter((id) => id !== allergenId));
  }

  return (
    <div className="flex flex-col gap-4">
      {mutation.isError ? <Alert tone="danger">{mapMenuPatchError(mutation.error)}</Alert> : null}

      {menu.allergens.length === 0 ? (
        <p className="text-sm text-slate-500">Este menú no tiene alérgenos declarados.</p>
      ) : (
        <ul className="flex flex-wrap gap-2">
          {menu.allergens.map((allergen) => (
            <li
              key={allergen.id}
              className="flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-900"
            >
              {allergen.name}
              {!readOnly ? (
                <button
                  type="button"
                  aria-label={`Quitar ${allergen.name}`}
                  onClick={() => removeAllergen(allergen.id)}
                  className="text-slate-500 hover:text-slate-900"
                >
                  ×
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {!readOnly ? (
        <div className="flex items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="menu-allergen-select" className="text-sm font-medium text-slate-900">
              Agregar alérgeno
            </label>
            <select
              id="menu-allergen-select"
              value={selected}
              onChange={(event) => setSelected(event.target.value)}
              className="h-11 rounded-md border border-slate-300 px-3 text-sm"
            >
              <option value="">Seleccioná un alérgeno</option>
              {availableOptions.map((allergen) => (
                <option key={allergen.id} value={allergen.id}>
                  {allergen.name}
                </option>
              ))}
            </select>
          </div>
          <Button
            type="button"
            loading={mutation.isPending}
            disabled={selected === ''}
            onClick={addAllergen}
          >
            Agregar
          </Button>
        </div>
      ) : null}
    </div>
  );
}
